import React, { useState, useMemo } from "react";
import { Bell, UserCheck, UserX, MessageSquare, AtSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUserMessages } from "@/hooks/useUserMessages";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMessagingStore } from "@/store/useMessagingStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications, AppNotification } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";

const UserNotifications: React.FC = () => {
  const { connections, updateConnectionStatus, messages, unreadMessageCount, pendingConnectionCount } = useUserMessages();
  const { notifications, unreadCount: unreadMentionsCount, markAsRead: markNotificationAsRead } = useNotifications();
  const { user } = useAuth();
  const { openMessaging } = useMessagingStore();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // These are still needed for displaying the lists within the tabs
  const pendingIncoming = useMemo(() => {
    const pending = connections.filter(
      (c) => c.status === "pending" && c.connected_user_id === user?.id
    );
    return pending;
  }, [connections, user]);

  const unreadMessages = useMemo(() => {
    const uniqueSenders = new Map<string, (typeof messages)[0]>();
    messages
      .filter((m) => m.recipient_id === user?.id && !m.read)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .forEach((m) => {
        if (!uniqueSenders.has(m.sender_id)) {
          uniqueSenders.set(m.sender_id, m);
        }
      });
    return Array.from(uniqueSenders.values());
  }, [messages, user]);

  // Calculate total notifications directly from the hook's counts
  const totalNotifications = unreadMessageCount + pendingConnectionCount + unreadMentionsCount;

  const handleAccept = async (connectionId: string) => {
    await updateConnectionStatus(connectionId, "accepted");
  };

  const handleDecline = async (connectionId: string) => {
    await updateConnectionStatus(connectionId, "declined");
  };

  const handleMessageClick = (clickedMessageId: string, senderId: string, senderName: string) => {
    console.log("UserNotifications: handleMessageClick called for:", { senderId, senderName });
    openMessaging({ id: senderId, name: senderName });
    setOpen(false); // Close the popover when a message is clicked
  };

  const handleMentionClick = (notification: AppNotification) => {
    markNotificationAsRead(notification.id);
    if (notification.data.channel_slug) {
      navigate(`/lobby/${notification.data.channel_slug}`);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full">
          <Bell className="h-5 w-5" />
          {totalNotifications > 0 && (
            <span className="absolute -top-1 -right-1">
              <Badge
                className="rounded-full px-1.5 py-0.5 text-xs bg-primary text-primary-foreground border-none"
                variant="default"
              >
                {totalNotifications}
              </Badge>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <Tabs defaultValue="connections" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="connections">
              Requests
              {pendingConnectionCount > 0 && (
                <Badge className="ml-2 bg-primary text-primary-foreground border-none">{pendingConnectionCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages">
              Messages
              {unreadMessageCount > 0 && (
                <Badge className="ml-2 bg-primary text-primary-foreground border-none">{unreadMessageCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="mentions">
              Mentions
              {unreadMentionsCount > 0 && (
                <Badge className="ml-2 bg-primary text-primary-foreground border-none">{unreadMentionsCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="connections">
            <div className="p-3 border-b font-semibold flex items-center gap-2">
              <UserCheck className="h-4 w-4 mr-1" />
              Connection Requests
            </div>
            <ScrollArea className="max-h-72">
              {pendingIncoming.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No new connection requests.
                </div>
              ) : (
                <div className="divide-y">
                  {pendingIncoming.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {c.connected_user_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {c.connected_user_name || "User"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          wants to connect
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-green-600"
                        onClick={() => handleAccept(c.id)}
                        title="Accept"
                      >
                        <UserCheck className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => handleDecline(c.id)}
                        title="Decline"
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          <TabsContent value="messages">
            <div className="p-3 border-b font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4 mr-1" />
              Unread Messages
            </div>
            <ScrollArea className="max-h-72">
              {unreadMessages.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No new messages.
                </div>
              ) : (
                <div className="divide-y">
                  {unreadMessages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer"
                      onClick={() => handleMessageClick(msg.id, msg.sender_id, msg.sender_name || 'User')}
                    >
                      <Avatar className="h-8 w-8">
                         <AvatarFallback>
                          {msg.sender_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {msg.sender_name || "User"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          <TabsContent value="mentions">
            <div className="p-3 border-b font-semibold flex items-center gap-2">
              <AtSign className="h-4 w-4 mr-1" />
              Mentions
            </div>
            <ScrollArea className="max-h-72">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No new mentions.
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id} 
                      className={`flex items-start gap-3 p-3 hover:bg-muted cursor-pointer ${!notif.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      onClick={() => handleMentionClick(notif)}
                    >
                      <AtSign className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">
                          <span className="font-semibold">{notif.data.mentioned_by_user_name}</span>
                          <span> mentioned you in </span>
                          <span className="font-semibold">#{notif.data.channel_name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground italic mt-1">
                          "{notif.data.message_content_preview}"
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};

export default UserNotifications;